import React from 'react';

import { formatMoney, useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, HStack, Heading, Separator, Text, Tooltip, VStack } from '@betterment/samba-react';
import { vstack } from '@betterment/samba-styled-system/patterns';
import { useFeatureFlag } from '@betterment/shared-components';

import { BoostedTag } from './components/BoostedTag';
import { BoostApyLink, OpenCashAccountLink, UserPerformanceLink } from './components/Links';
import { TaxLossHarvestingRow } from './components/TaxLossHarvestingRow';
import { PerformanceDocument } from './graphql/query.performance.generated';

import { CompactCardLoader } from '../../Shared/components-v2/shimmer-loaders/compact-card-loader';

const PerformanceCard: React.FC = () => {
  const { data } = useGraphQLQuery(PerformanceDocument, { throwOnError: true });
  const { enabled: isDirectTradingInUserPeformanceEnabled } = useFeatureFlag({
    splitName: 'retail.direct_trading_in_user_performance_q4_2025_enabled',
    context: 'RetailsContentCard',
  });

  if (!data) return <CompactCardLoader />;

  const { totals, accounts } = data.performance;
  const { investing, interest } = totals;

  if (!investing && !interest) {
    return null;
  }

  const isCashAllowed = data.productOnboardingOptions.find(o => o.legacyProductCategory === 'cash');

  const apys = accounts.filter(a => 'apy' in a).map(a => a.apy);
  const soleApyByRate = new Set(apys.map(a => a.rate)).size === 1 ? apys[0] : null;

  // TODO: Remove this unpleasant hack once we include SDI accounts in performance
  const hasDirectTradingAccounts =
    !isDirectTradingInUserPeformanceEnabled &&
    data.envelopes.some(envelope => envelope.accounts.some(account => account.__typename === 'DirectTradingAccount'));

  const sdiExclusionMessage =
    "We're working on adding self-directed investing account details to overall performance. You can access your self-directed investing performance in your account overview.";
  const performanceSubHeader = hasDirectTradingAccounts ? 'Excludes self-directed investing' : 'All time';

  return (
    <Card data-testid="specPagePerformanceCard">
      <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Heading as="h2" variant="title200">
            Performance
          </Heading>
          <HStack css={{ gap: 'space.50' }}>
            <Text variant="text50" color="text.weak">
              {performanceSubHeader}
            </Text>
            {hasDirectTradingAccounts && (
              <Tooltip>
                <Tooltip.Trigger asChild={true}>
                  <Tooltip.IconButton name="InfoCircle" css={{ cursor: 'default' }} />
                </Tooltip.Trigger>
                <Tooltip.Content>{sdiExclusionMessage}</Tooltip.Content>
              </Tooltip>
            )}
          </HStack>
        </Box>
        <UserPerformanceLink />
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content className={vstack({ gap: 'space.100', alignItems: 'stretch', padding: 'space.300' })}>
        {investing && (
          <HStack css={{ justifyContent: 'space-between' }}>
            <Text>Total earned</Text>
            <Text css={{ pl: 'space.300', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(investing.earned)}</Text>
          </HStack>
        )}
        {investing?.dividends && (
          <HStack css={{ justifyContent: 'space-between' }}>
            <Text>Dividends earned</Text>
            <Text css={{ pl: 'space.300', fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(investing.dividends.total)}
            </Text>
          </HStack>
        )}
        {isCashAllowed && (
          <HStack css={{ justifyContent: 'space-between' }}>
            <VStack>
              <Text as="p">Cash interest paid</Text>
              {soleApyByRate && (
                <HStack>
                  <Text variant="text50" color="text.weak">
                    {soleApyByRate.rate}% APY*
                  </Text>
                  {soleApyByRate.isBoostActive && <BoostedTag />}
                  {soleApyByRate.isBoostEligible && <BoostApyLink />}
                </HStack>
              )}
            </VStack>
            {interest ? (
              <Text css={{ pl: 'space.300', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(interest.paid)}</Text>
            ) : (
              <OpenCashAccountLink />
            )}
          </HStack>
        )}
        {investing && (
          <TaxLossHarvestingRow
            taxLossesHarvested={investing.taxLossesHarvested}
            allTimeEstimatedTaxSavings={investing.allTimeTlhEstimatedTaxSavings}
          />
        )}
      </Card.Content>
    </Card>
  );
};

export default PerformanceCard;
